import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import * as React from "react";

import { Draggable, DragItem } from "@/shared/components/visualization/DnD";
import { Expandable } from "@/shared/components/visualization/Expandable";
import { type TopicNode } from "@/shared/domain/topics";

import { TopicTreeNode } from "./buildTopicTree";
import { MessagePath } from "./MessagePath";
import { ShowHiddenToggle } from "./ShowHiddenToggle";
import styles from "./Topic.module.css";
import { TopicLabel } from "./TopicLabel";
import { useExpandable, useVisibleChildren } from "./TreeNodeHooks";

interface TopicProps {
  node: TopicTreeNode<TopicNode>;
}

/**
 * Render a Topic and its associated MessagePaths.
 */
export function Topic(props: TopicProps) {
  const { node } = props;
  const { data: topicNode } = node;

  const { expanded, setExpanded } = useExpandable(node.showChildren);
  const {
    showHidden,
    setShowHidden,
    numHidden,
    children: visibleChildren,
    showHiddenChildrenToggle,
  } = useVisibleChildren(node.children);

  /**
   * Memoizing children because re-rendering MessagePaths is expensive. The ContextMenu
   * component makes an expensive sharesRepresentationWithBinaryField check on every render.
   */
  const children = React.useMemo(
    function renderNestedPaths() {
      return visibleChildren.map((child) => (
        <MessagePath key={child.data.id} node={child} />
      ));
    },
    [visibleChildren],
  );

  const toggleExpanded = () => {
    if (node.children.length === 0) {
      return;
    }
    setExpanded(!expanded);

    if (expanded && !node.showChildren) {
      // Reset the option to show hidden children when the user collapses the node
      // and the node doesn't have a reason to show children
      setShowHidden(false);
    } else if (numHidden === node.children.length) {
      // Show all children if user expands the node when all children are filtered out
      setShowHidden(true);
    }
  };

  const baseLabel = (
    <TopicLabel
      dataType={topicNode.data.schema_name || ""}
      label={topicNode.label}
      match={node.match}
    />
  );

  const label = (
    <Draggable<TopicNode>
      id={`tree-${topicNode.id}`}
      item={{ type: DragItem.TreeNode, data: topicNode }}
      className={styles.draggable}
    >
      <Expandable<TopicTreeNode<TopicNode>>
        data={node}
        StartIcon={expanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon}
        onClick={toggleExpanded}
        className={styles.topicIndent}
        iconClassName={styles.expandIcon}
      >
        {baseLabel}
      </Expandable>
    </Draggable>
  );

  // Hide the topic if it's been filtered out
  if (node.hidden) {
    return null;
  }

  return (
    <div className={styles.container}>
      {label}
      {expanded && (
        <div className={styles.messagePathContainer}>
          {children}
          {showHiddenChildrenToggle && (
            <ShowHiddenToggle
              numHidden={numHidden}
              showHidden={showHidden}
              onToggle={() => setShowHidden(!showHidden)}
            />
          )}
        </div>
      )}
    </div>
  );
}
